import { For, Show } from 'solid-js'

import { dispatchAppAction } from '../../runtimeBridge'
import { useI18n } from '../../i18n'
import { getLocale } from '../../i18n'
import { COLOR_THEMES, colorTheme, selectColorTheme } from '../../state/colorThemeStore'
import { openShareOverlay } from '../../state/shareOverlayStore'

const LOCALE_LINKS = ['en', 'de', 'fr', 'es'] as const
const APP_SHARE_BASE_URL = 'https://fasting-timer.pages.dev/'
const COLOR_THEME_LABEL_KEYS = {
   midnight: 'Midnight',
   paper: 'Paper',
} as const

/**
 * Renders the app footer (share link, locale override, theme switch).
 *
 * @returns The footer element.
 */
const Footer = () => {
   const t = useI18n()

   return (
      <footer class="mt-8 w-full text-sm text-ink-faint">
         <div class="flex flex-col items-center gap-4">
            <button
               id="global-share-btn"
               type="button"
               onClick={() =>
                  openShareOverlay({
                     title: t('Share Faster · Leaner · Stronger'),
                     description: t(
                        'Scan this QR code or open the link below on another device.',
                     ),
                     url: APP_SHARE_BASE_URL,
                     source: 'app',
                  })
               }
               class="inline-flex items-center rounded-full border border-edge bg-surface-raised px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-soft transition hover:bg-surface-control focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-surface"
            >
               {t('Share link to this application')}
            </button>
            <nav
               id="color-theme-override"
               class="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted"
               aria-label={t('Color theme')}
            >
               <span class="normal-case text-ink-faint">{t('Theme:')}</span>
               <For each={COLOR_THEMES}>
                  {(theme, index) => (
                     <>
                        <button
                           type="button"
                           data-color-theme={theme}
                           onClick={() => selectColorTheme(theme)}
                           aria-pressed={colorTheme() === theme ? 'true' : 'false'}
                           class="transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                           classList={{
                              'text-accent-active': colorTheme() === theme,
                              'text-ink-muted hover:text-ink-soft':
                                 colorTheme() !== theme,
                           }}
                        >
                           {t(COLOR_THEME_LABEL_KEYS[theme])}
                        </button>
                        <Show when={index() < COLOR_THEMES.length - 1}>
                           <span class="text-ink-faint">-</span>
                        </Show>
                     </>
                  )}
               </For>
            </nav>
            <nav
               id="locale-override"
               class="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted"
               aria-label="Language override"
            >
               <For each={LOCALE_LINKS}>
                  {(code, index) => (
                     <>
                        <button
                           type="button"
                           data-locale={code}
                           onClick={() =>
                              dispatchAppAction({
                                 action: 'select-locale',
                                 locale: code,
                              })
                           }
                           aria-pressed={getLocale() === code ? 'true' : 'false'}
                           class="locale-override-btn transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                           classList={{
                              'text-accent-active text-ink-soft font-semibold':
                                 getLocale() === code,
                              'text-ink-muted hover:text-ink-soft': getLocale() !== code,
                           }}
                        >
                           {code}
                        </button>
                        <Show when={index() < LOCALE_LINKS.length - 1}>
                           <span class="text-ink-faint">-</span>
                        </Show>
                     </>
                  )}
               </For>
            </nav>
         </div>
      </footer>
   )
}

export default Footer
